import { useCallback, useEffect, useMemo, useState } from "react";
import { useSnackbar } from "notistack";
import { useQueryClient } from "@tanstack/react-query";
import { Trans, useTranslation } from "react-i18next";

import i18n from "../../i18n";
import { detectionSourceLabel, isAutoDetected } from "../../DTOs/Common/DetectionSource";
import { GeoRehandleDTO } from "../../DTOs/Geology/GeoRehandleDTO";
import { geologyAjax } from "../../ajax/GeologyAjax";
import { ButtonCrumb } from "../../components/Breadcrumbs/ButtonCrumb";
import { DateSelectCrumb } from "../../components/Breadcrumbs/DateSelectCrumb";
import { DataTable, TableRow } from "../../components/DataTable/DataTable";
import { useDataTable, type DataTableFilteredRequest } from "../../components/DataTable/TableHook";
import { useAutoPageSize } from "../../components/Hooks/useAutoPageSize";
import { setCurrentView } from "../../state/reducers/viewReducer";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../state/store";
import { Breadcrumbs } from "../../components/Breadcrumbs/Breadcrumbs";
import { useOptions } from "../../components/Hooks/useOptions";
import { useOnlineStatus } from "../../components/Hooks/useOnlineStatus";
import { useMovementEntryConfig } from "../../components/Hooks/useMovementEntryConfig";
import { useTelemetryEnabled } from "../../components/Hooks/useTelemetryEnabled";
import { sumCachedLoads } from "../../offline/patchCachedDiggingSummary";
import { DeleteButton } from "../../components/Buttons/DeleteButton";
import Button from "../../components/Buttons/Button";
import { RehandleGroupsTable } from "../GeologyRehandle/RehandleGroupsTable";
import SaveButton from "../../components/Buttons/SaveButton";
import { VerificationIndicator } from "../../components/VerificationIndicator/VerificationIndicator";
import { InsertGeoCrushingRow } from "./InsertGeologyCrushingRow";
import { GeoCrushingLoaderDTO } from "../../DTOs/Geology/GeoCrushingLoaderDTO";
import { GeoRehandleTruckingTruckDTO } from "../../DTOs/Geology/GeoRehandleTruckingTruckDTO";
import { setSelectedDate, setSelectedShift } from "../../state/reducers/shiftReducer";
import useShiftTimes from "../../components/Hooks/useShiftTimes";
import { TripleShift } from "../../types/Shift";
import { LocationDTO } from "../../DTOs/Production/LocationDTO";
import LoaderCell from "./TableCellsComponents/LoaderCell";
import MovementToCell from "./TableCellsComponents/MovementToCell";
import MovementFromCell from "./TableCellsComponents/MovementFromCell";
import PlantModelSelect from "../GeologyRehandle/TableCellsComponents/PlantModelSelect";
import TruckSelect from "../GeologyRehandle/TableCellsComponents/TruckSelect";
import ShiftHourSelect from "../GeologyRehandle/TableCellsComponents/ShiftHourSelect";
import MovementTimeSelect from "../GeologyRehandle/TableCellsComponents/MovementTimeSelect";
import { movementColumnLabel, movementEntryErrors, movementLayout, plantModelOptions, truckOptions } from "../GeologyRehandle/movementEntry";
import { useMovementTiming } from "../GeologyRehandle/useMovementTiming";
import { ShiftSelectCrumb } from "../../components/Breadcrumbs/Shift/ShiftSelectCrumb";
import { GetGeoRehandlesPagedResultDTO } from "../../DTOs/Geology/GetGeoRehandlesPagedResultDTO";
import GeologyCrushingTableFooter from "./GeologyCrushingTableFooter";
import ShiftTableNoResults from "../../components/ShiftTableNoResults/ShiftTableNoResults";
import { TablePage } from "../../components/Layout/TablePage";
import { useSignalRContext } from "../../components/Contexts/SignalRContext";
import { geoRehandleMutations } from "../../offline/geoRehandleMutations";
import { useIsReadOnly } from "../../utilities/helpers/roleUtilities";

type GeologyCrushingTableMetaData = {
  totalLoads: number
  totalTonnes: number
}

const GeologyCrushing: React.FC = () => {
  const { t } = useTranslation("geology");
  const currentView = useSelector((state: RootState) => state.view.currentView);
  const isReadOnly = useIsReadOnly();

  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { connection } = useSignalRContext();
  const queryClient = useQueryClient();
  const { selectedDate, selectedShift } = useShiftTimes();
  const isOnline = useOnlineStatus();
  const [localSelectedShift, _setLocalSelectedShift] = useState<TripleShift>(selectedShift)

  // Note: not persisted, the same as Rehandle. Grouping is how somebody wants to look at THIS shift.
  const [isGrouped, setIsGrouped] = useState(false);
  const [insertRowState, setInsertRowState] = useState<GeoRehandleDTO>(new GeoRehandleDTO({
    geoRehandleId: 0,
    operationId: 0,
    bucketTypeId: 0,
    movementFromId: 0,
    movementToId: 0
  }));

  const setLocalSelectedShift = (newShift: TripleShift) => {
    _setLocalSelectedShift(newShift);

    if (newShift === "DAY" || newShift === "NIGHT") {
      dispatch(setSelectedShift(newShift));
    }
  };

  const {
    options: crushingMovementFromLocationOptions,
    isLoading: isCrushingMovementFromLocationOptionsLoading
  } = useOptions<LocationDTO, LocationDTO>(
    ['geology', 'crushingFromLocations', selectedDate.toISODate()],
    () => geologyAjax.getCrushingMovementFromLocations(selectedDate),
    x => x,
  );

  const {
    options: crushingMovementToLocationOptions,
    isLoading: isCrushingMovementToLocationOptionsLoading
  } = useOptions<LocationDTO, LocationDTO>(
    ['geology', 'crushingToLocations', selectedDate.toISODate()],
    () => geologyAjax.getCrushingMovementToLocations(selectedDate),
    x => x,
  );

  const {
    options: loaderOptions,
    isLoading: isLoaderOptionsLoading
  } = useOptions<GeoCrushingLoaderDTO, GeoCrushingLoaderDTO>(
    ['geology', 'crushingLoaders', selectedDate.toISODate(), localSelectedShift],
    () => geologyAjax.getCrushingLoaders(selectedDate, localSelectedShift),
    x => x,
  );

  const entryConfig = useMovementEntryConfig('crushing');
  const telemetryEnabled = useTelemetryEnabled();
  const layout = movementLayout(entryConfig);
  const { zone, hours, liveHourIndex, shiftWindow } = useMovementTiming(selectedDate, localSelectedShift);
  const defaultInsertHour = layout.timeColumn === "Hour" && liveHourIndex >= 0 ? liveHourIndex + 1 : undefined;

  const {
    options: crushingTrucks,
    isLoading: isTruckOptionsLoading
  } = useOptions<GeoRehandleTruckingTruckDTO, GeoRehandleTruckingTruckDTO>(
    ['geology', 'rehandleTrucks', selectedDate.toISODate()],
    () => geologyAjax.getRehandleTruckingTrucks(selectedDate),
    x => x,
    { enabled: entryConfig.carriedBy !== 'DiggerLoader' || isGrouped },
  );

  const trucks = useMemo(() => truckOptions(crushingTrucks), [crushingTrucks]);
  const plantModels = useMemo(() => plantModelOptions(crushingTrucks), [crushingTrucks]);

  const selectGeologyCrushingTableMetaData = useCallback((response: GetGeoRehandlesPagedResultDTO) => ({
    totalTonnes: response.totalTonnes,
    totalLoads: response.totalLoads
  }), []);

  const { pageSize: fitPageSize, onAutoPageSize } = useAutoPageSize();
  const getRows = useCallback((req: DataTableFilteredRequest) => geologyAjax.getGeoCrushingEntries(selectedDate, localSelectedShift, req), [selectedDate, localSelectedShift]);

  const {
    rows,
    isLoading,
    isBackgroundFetching,
    error,
    pageNumber,
    pageSize,
    totalCount,
    totalPages,
    sortField,
    sortDirection,
    setPage,
    appendRowSource,
    removeRowSource,
    updateRowSource,
    invalidate,
    tableMetaData
  } = useDataTable<GeoRehandleDTO, GeologyCrushingTableMetaData, GetGeoRehandlesPagedResultDTO>({
    queryKey: ['geology', 'crushing', selectedDate.toISODate(), localSelectedShift],
    fetchFn: getRows,
    initialPage: 1,
    pageSize: fitPageSize,
    clientPaginate: true,
    selectMeta: selectGeologyCrushingTableMetaData
  });

  const handleUpdateRow = useCallback(async (row: GeoRehandleDTO) => {
    let isValid = true;

    if (!row.operationId) {
      isValid = false;
      enqueueSnackbar(t("movement.selectLoader"), { variant: 'error' });
    }
    movementEntryErrors(row, entryConfig, false, { plantModels, trucks, carriesOwnLoad: true }).forEach(message => {
      isValid = false;
      enqueueSnackbar(message, { variant: 'error' });
    });
    if (!row.movementFromId) {
      isValid = false;
      enqueueSnackbar(t("movement.selectFrom"), { variant: 'error' });
    }
    if (!row.loads) {
      isValid = false;
      enqueueSnackbar(t("movement.enterLoads"), { variant: 'error' });
    }
    if (!row.movementToId) {
      isValid = false;
      enqueueSnackbar(t("crushing.selectCrushingSite"), { variant: 'error' });
    }
    if (row.movementFromId === row.movementToId) {
      isValid = false;
      enqueueSnackbar(t("movement.selectDifferentTo"), { variant: 'error' });
    }

    if (!isValid || row.geoRehandleId === 0) return;

    const response = await geoRehandleMutations.updateGeoRehandle(row)

    if (!response.success && !response.data) {
      response.errors?.forEach((error, index) => {
        enqueueSnackbar(error.description, { variant: 'error', autoHideDuration: 5000 + index * 1500 })
      })

      invalidate();
      return false;
    }

    enqueueSnackbar(t("crushing.updated"), { variant: 'success' });

    const { rehandle, totalLoads, totalTonnes } = response.data!;
    updateRowSource((r) => r.geoRehandleId === rehandle!.geoRehandleId, rehandle!, { totalLoads, totalTonnes });

    return true;
  }, [enqueueSnackbar, entryConfig, invalidate, plantModels, trucks, updateRowSource, t]);

  const handleAddRow = useCallback(async () => {
    let isValid = true;
    const row = { ...insertRowState, hour: insertRowState.hour ?? defaultInsertHour };

    if (!row.operationId) {
      enqueueSnackbar(t("movement.selectLoader"), { variant: 'error', autoHideDuration: 3000 });
      isValid = false;
    }

    movementEntryErrors(row, entryConfig, true, { plantModels, trucks, carriesOwnLoad: true }).forEach(message => {
      enqueueSnackbar(message, { variant: 'error', autoHideDuration: 3000 });
      isValid = false;
    });

    if (!row.movementFromId) {
      enqueueSnackbar(t("movement.selectFrom"), { variant: 'error', autoHideDuration: 3000 });
      isValid = false;
    }

    if (!row.loads) {
      enqueueSnackbar(t("movement.enterLoads"), { variant: 'error', autoHideDuration: 3000 });
      isValid = false;
    }

    if (!row.movementToId) {
      enqueueSnackbar(t("crushing.selectCrushingSite"), { variant: 'error', autoHideDuration: 3000 });
      isValid = false;
    }

    if (!isValid) return false;

    const response = await geoRehandleMutations.addGeoRehandle(row, selectedDate, localSelectedShift);

    if (!response.success && !response.data) {
      response.errors?.forEach((error, index) => {
        enqueueSnackbar(error.description, { variant: 'error', autoHideDuration: 5000 * (index + 1) });
      });
      return false;
    }

    enqueueSnackbar(t("crushing.added"), { variant: 'success' });

    const { rehandle, totalLoads, totalTonnes } = response.data!;
    appendRowSource(rehandle!, { totalLoads, totalTonnes });
    setInsertRowState(new GeoRehandleDTO({
      geoRehandleId: 0,
      operationId: 0,
      bucketTypeId: 0,
      movementFromId: 0,
      movementToId: 0,
      hour: row.hour
    }));

    return true;
  }, [insertRowState, defaultInsertHour, entryConfig, plantModels, trucks, selectedDate, localSelectedShift, enqueueSnackbar, appendRowSource, t]);

  const deleteRow = useCallback(async (rowId: number) => {
    const response = await geoRehandleMutations.deleteGeoRehandle(rowId);

    if (!response.success && !response.data) {
      response.errors?.forEach((error, index) => {
        enqueueSnackbar(error.description, { variant: 'error', autoHideDuration: 5000 + index * 1500 })
      })

      return false;
    }

    const { totalLoads, totalTonnes } = response.data!;
    removeRowSource((r) => r.geoRehandleId === rowId, { totalLoads, totalTonnes });
    enqueueSnackbar(t("crushing.deleted"), { variant: 'success', autoHideDuration: 3000 })

    return true;
  }, [enqueueSnackbar, removeRowSource, t]);

  const verifyRow = useCallback(async (entry: GeoRehandleDTO) => {
    let response;
    try {
      response = await geologyAjax.verifyGeoRehandle(entry.geoRehandleId!);
    } catch {
      enqueueSnackbar(t("movement.acceptUnreachable"), { variant: "error", autoHideDuration: 6000 });
      return;
    }

    if (!response.success) {
      response.errors?.forEach(error =>
        enqueueSnackbar(error.description, { variant: "error", autoHideDuration: 6000 }));
      return;
    }

    updateRowSource(
      (r: GeoRehandleDTO) => r.geoRehandleId === entry.geoRehandleId,
      { ...entry, isVerified: true } as GeoRehandleDTO);
    invalidate();

    enqueueSnackbar(t("movement.rowAccepted"), { variant: "success", autoHideDuration: 2500 });
  }, [enqueueSnackbar, invalidate, updateRowSource, t]);

  const translateRows = useCallback((entry: GeoRehandleDTO): TableRow => {
    return {
      id: entry.geoRehandleId ?? 0,
      values: [
        ...(layout.timeColumn === "Hour" ? [{
          name: "Hour",
          editable: false,
          type: "custom" as const,
          value: entry.hour,
          tailwindClasses: `${layout.time} py-1 pl-1`,
          render: () => (
            <ShiftHourSelect
              hour={entry.hour}
              hours={hours}
              liveHourIndex={liveHourIndex}
              disabled={isReadOnly}
              onChange={hour => handleUpdateRow({ ...entry, hour })}
            />
          )
        }] : []),
        ...(layout.timeColumn === "Time" ? [{
          name: "Time",
          editable: false,
          type: "custom" as const,
          value: entry.movementTimeUtc,
          tailwindClasses: `${layout.time} py-1 pl-1`,
          render: () => (
            <MovementTimeSelect
              movementTimeUtc={entry.movementTimeUtc}
              shiftWindow={shiftWindow}
              zone={zone}
              disabled={isReadOnly}
              saveOnAccept
              onChange={movementTimeUtc => handleUpdateRow({ ...entry, movementTimeUtc })}
            />
          )
        }] : []),
        {
          name: "Loader",
          editable: false,
          type: "custom",
          value: entry.bucketTypeId,
          tailwindClasses: `${layout.machine} py-1${layout.timeColumn ? "" : " pl-1"}`,
          render: () => (
            <LoaderCell
              entry={entry}
              loaderOptions={loaderOptions}
              isLoaderOptionsLoading={isLoaderOptionsLoading}
              handleUpdateRow={handleUpdateRow}
              isReadOnly={isReadOnly}
            />
          )
        },
        ...(layout.carrierColumn === "Plant Model" ? [{
          name: "Plant Model",
          editable: false,
          type: "custom" as const,
          value: entry.bucketTypeId,
          tailwindClasses: `${layout.carrier} py-1`,
          render: () => (
            <PlantModelSelect
              bucketTypeId={entry.bucketTypeId}
              plantModels={plantModels}
              isLoading={isTruckOptionsLoading}
              disabled={isReadOnly}
              onChange={bucketTypeId => handleUpdateRow({ ...entry, bucketTypeId })}
              allowOwnLoad
              onOwnLoad={() => handleUpdateRow({ ...entry, bucketTypeId: undefined })}
            />
          )
        }] : []),
        ...(layout.carrierColumn === "Truck" ? [{
          name: "Truck",
          editable: false,
          type: "custom" as const,
          value: entry.truckPlantId,
          tailwindClasses: `${layout.carrier} py-1`,
          render: () => (
            <TruckSelect
              truckPlantId={entry.truckPlantId}
              trucks={trucks}
              isLoading={isTruckOptionsLoading}
              disabled={isReadOnly}
              onChange={truck => handleUpdateRow({ ...entry, truckPlantId: truck.plantId, bucketTypeId: truck.bucketTypeId })}
              allowOwnLoad
              onOwnLoad={() => handleUpdateRow({ ...entry, truckPlantId: undefined, bucketTypeId: undefined })}
            />
          )
        }] : []),
        {
          name: "Movement From",
          editable: false,
          type: "custom",
          value: entry.movementFromId,
          tailwindClasses: layout.movement,
          render:
            () => (
              <MovementFromCell
                entry={entry}
                crushingMovementFromLocationOptions={crushingMovementFromLocationOptions}
                isCrushingMovementFromLocationOptionsLoading={isCrushingMovementFromLocationOptionsLoading}
                handleUpdateRow={handleUpdateRow}
                isReadOnly={isReadOnly}
              />
            )
        },
        {
          name: "Loads",
          editable: true,
          type: "integer",
          value: entry.loads,
          tailwindClasses: "basis-[10%] py-1",
          disabled: isReadOnly,
          onChangeDebounced: async (_, newValue) => await handleUpdateRow({ ...entry, loads: newValue }),
          onChangeDebouncedMs: 1200,
        },
        {
          name: "Movement To",
          editable: false,
          type: "custom",
          value: entry.movementToId,
          tailwindClasses: layout.movement,
          render:
            () => (
              <MovementToCell
                entry={entry}
                crushingMovementToLocationOptions={crushingMovementToLocationOptions}
                isCrushingMovementToLocationOptionsLoading={isCrushingMovementToLocationOptionsLoading}
                handleUpdateRow={handleUpdateRow}
                isReadOnly={isReadOnly}
              />
            )
        },
        ...(telemetryEnabled ? [{
          // Note: the same shape Stoppages.tsx and TrucksTable.tsx use for provenance, so an inferred
          // rehandle reads the way inferred data reads everywhere else in Eclipse rather than announcing
          // itself a third way. Both this screen and Crushing show it: they are the same tblGeoRehandle
          // rows split on whether the destination is a crusher, so marking only one would leave inferred
          // stockpile-to-crusher rows looking hand-entered.
          name: 'Source',
          type: 'custom' as const,
          value: entry.isVerified === false ? 'Needs checking' : '',
          tailwindClasses: 'w-full basis-[10%]',
          render: () => (
            <div className="flex items-center justify-center gap-1.5 w-full">
              <div className="text-theme-text-muted text-xs">
                {detectionSourceLabel(entry.detectionSource)}
              </div>
              {/* Note: the mark IS the accept control. Clicking it accepts the row as it stands, which is
                  the only way a row the FMS got right ever leaves the queue - editing clears the flag too,
                  but only as a side effect of changing something. */}
              {isAutoDetected(entry.detectionSource) && (
                <VerificationIndicator
                  isVerified={entry.isVerified !== false}
                  onAccept={() => verifyRow(entry)}
                  subject={t("crushing.verifySubject")}
                  disabled={isReadOnly || !isOnline}
                />
              )}
            </div>
          )
        }] : []),
        {
          name: 'Actions',
          type: 'custom',
          value: 'custom',
          tailwindClasses: 'basis-[10%] pr-1',
          render: (_, tableRow) =>
            <div className="flex justify-center flex-wrap gap-1">
              <DeleteButton
                disabled={isReadOnly}
                handleDelete={() => deleteRow(tableRow.id)}
              />
            </div>
        }
      ]
    }
  }, [
    layout,
    hours,
    liveHourIndex,
    shiftWindow,
    zone,
    plantModels,
    trucks,
    isTruckOptionsLoading,
    telemetryEnabled,
    loaderOptions,
    isLoaderOptionsLoading,
    handleUpdateRow,
    isReadOnly,
    crushingMovementFromLocationOptions,
    isCrushingMovementFromLocationOptionsLoading,
    crushingMovementToLocationOptions,
    isCrushingMovementToLocationOptionsLoading,
    deleteRow,
    isOnline,
    verifyRow,
    t
  ]);

  const displayData = useMemo(() => rows.map(translateRows), [rows, translateRows]);

  // Note: built from the pickers this screen already loads, so grouping adds no extra fetch. Crushing's
  // location options are LocationDTOs rather than the value/label pairs Rehandle maps to, which is why the
  // grouped table takes label functions rather than the option arrays themselves.
  const machineLabel = useCallback(
    (operationId: number) => loaderOptions.find(l => l.operationId === operationId)?.plantRef ?? "-",
    [loaderOptions]);

  const locationLabel = useCallback((locationId?: number) => {
    if (locationId == null) return "-";
    return crushingMovementFromLocationOptions.find(l => l.id === locationId)?.name
      ?? crushingMovementToLocationOptions.find(l => l.id === locationId)?.name
      ?? "-";
  }, [crushingMovementFromLocationOptions, crushingMovementToLocationOptions]);

  const bucketTypeLabel = useCallback((bucketTypeId?: number) => {
    if (bucketTypeId == null) return "-";
    return plantModels.find(m => m.bucketTypeId === bucketTypeId)?.bucketType
      ?? loaderOptions.find(l => l.bucketTypeId === bucketTypeId)?.bucketType
      ?? "-";
  }, [plantModels, loaderOptions]);

  const handleAcceptAll = useCallback(async () => {
    const response = await geologyAjax.verifyAllGeoRehandles(selectedDate, localSelectedShift, true);

    if (!response.success) {
      response.errors?.forEach((error, i) =>
        enqueueSnackbar(error.description, { variant: "error", autoHideDuration: 4000 + i * 1000 }));
      return false;
    }

    const accepted = response.data ?? 0;
    enqueueSnackbar(
      accepted === 0 ? t("movement.nothingToCheck")
        : t("movement.rowsAccepted", { count: accepted }),
      { variant: "success", autoHideDuration: 3000 });

    invalidate();
    return true;
  }, [selectedDate, localSelectedShift, enqueueSnackbar, invalidate, t]);

  useEffect(() => {
    const newView = "GeologyCrushing";

    if (currentView !== newView) {
      dispatch(setCurrentView(newView));
    }
  }, [currentView, dispatch]);

  const handleGeoMovementsChanged = useCallback(() => {
    invalidate();
    queryClient.invalidateQueries({ queryKey: ['geology', 'crushingFromLocations'] });
    queryClient.invalidateQueries({ queryKey: ['geology', 'crushingToLocations'] });
  }, [invalidate, queryClient]);

  useEffect(() => {
    if (!connection) return;
    connection.on("DataUpdatedAsync", handleGeoMovementsChanged);
    return () => {
      connection.off("DataUpdatedAsync", handleGeoMovementsChanged);
    };
  }, [connection, handleGeoMovementsChanged]);

  return (
    <TablePage>
      <Breadcrumbs actions={telemetryEnabled ? (
        <Button
          variant={isGrouped ? "primary" : "secondary"}
          size="sm"
          onClick={() => setIsGrouped(g => !g)}
        >
          {isGrouped ? t("movement.showingGroups") : t("movement.groupMovements")}
        </Button>
      ) : undefined}>
        <DateSelectCrumb
          selectedDate={selectedDate}
          setSelectedDate={date => dispatch(setSelectedDate(date.toISODate()!))}
        />
        <ShiftSelectCrumb
          showAfternoonShift
          selectedShift={localSelectedShift}
          setSelectedShift={setLocalSelectedShift}
        />
        <ButtonCrumb buttonText={t("crushing.title")} />
      </Breadcrumbs>
      {telemetryEnabled && isGrouped ? (
        <RehandleGroupsTable
          selectedDate={selectedDate}
          selectedShift={localSelectedShift}
          isCrushing={true}
          machineLabel={machineLabel}
          locationLabel={locationLabel}
          bucketTypeLabel={bucketTypeLabel}
          isShiftLocked={isReadOnly}
          isOnline={isOnline}
        />
      ) : (
        <DataTable
          data={displayData}
          isLoading={isLoading}
          isBackgroundFetching={isBackgroundFetching}
          error={error}
          pageNumber={pageNumber}
          pageSize={pageSize}
          totalPages={totalPages}
          totalCount={totalCount}
          setPage={setPage}
          sortField={sortField}
          sortDirection={sortDirection}
          minTableWidthPixels={layout.timeColumn ? 1280 : 1080}
          fillHeight
          autoPageSize
          onAutoPageSize={onAutoPageSize}
          estimatedRowHeightPx={60}
          noResultsContent={<ShiftTableNoResults
            empty={t("crushing.empty")}
            entity={t("crushing.entity")}
            howToAdd={<Trans t={t} i18nKey="crushing.howToAdd" components={{ b: <span className="font-bold" /> }} />}
            addWrite="Geology.InsertGeologyRehandle"
          />}
          tableHeaderContent={telemetryEnabled ? (
            <div className="flex flex-col w-full items-center fade-up">
              <div className="container overflow-y-hidden">
                <div className="bg-theme-surface-2 px-2 py-2 border border-theme-table-border mb-4 compact:mb-2 shadow text-theme-text">
                  <div className="flex items-center justify-end gap-4">
                    <span className="opacity-70 text-sm">
                      {t("movement.telemetryNote")}
                    </span>
                    <SaveButton
                      buttonLabel={t("movement.acceptAll")}
                      confirmMessage={t("crushing.acceptAllConfirm")}
                      disabled={isReadOnly || !isOnline}
                      handleSave={handleAcceptAll}
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : undefined}
          tableFooterContent={<GeologyCrushingTableFooter
            totalLoads={isOnline
              ? tableMetaData?.totalLoads
              : sumCachedLoads(['dataTable', 'geology', 'crushing', selectedDate.toISODate(), localSelectedShift, 'all', false, null])}
            totalTonnes={tableMetaData?.totalTonnes}
            isLoading={isLoading}
          />}
          insertRowRenderer={() => <InsertGeoCrushingRow
            state={insertRowState}
            setState={setInsertRowState}
            loaderOptions={loaderOptions}
            isLoaderOptionsLoading={isLoaderOptionsLoading}
            layout={layout}
            plantModels={plantModels}
            trucks={trucks}
            isTruckOptionsLoading={isTruckOptionsLoading}
            allowOwnLoad
            hour={insertRowState.hour ?? defaultInsertHour}
            hours={hours}
            liveHourIndex={liveHourIndex}
            shiftWindow={shiftWindow}
            zone={zone}
            crushingMovementFromLocationOptions={crushingMovementFromLocationOptions}
            isCrushingMovementFromLocationOptionsLoading={isCrushingMovementFromLocationOptionsLoading}
            crushingMovementToLocationOptions={crushingMovementToLocationOptions}
            isCrushingMovementToLocationOptionsLoading={isCrushingMovementToLocationOptionsLoading}
            onAdd={handleAddRow}
            isReadOnly={isReadOnly}
            telemetryEnabled={telemetryEnabled}
          />}
          overrideHeaderCells={[
            ...(layout.timeColumn ? [{ name: layout.timeColumn, label: movementColumnLabel(layout.timeColumn), tailwindClasses: `${layout.time} py-1 pl-1` }] : []),
            { name: "Loader", label: t("movement.loader"), tailwindClasses: `${layout.machine} py-1${layout.timeColumn ? "" : " pl-1"}` },
            ...(layout.carrierColumn ? [{ name: layout.carrierColumn, label: movementColumnLabel(layout.carrierColumn), tailwindClasses: `${layout.carrier} py-1` }] : []),
            { name: "Movement From", label: t("movement.movementFrom"), tailwindClasses: layout.movement },
            { name: "Loads", label: i18n.t("fields.loads"), tailwindClasses: "basis-[10%] py-1" },
            { name: "Movement To", label: t("movement.movementTo"), tailwindClasses: layout.movement },
            ...(telemetryEnabled ? [{ name: "Source", label: i18n.t("fields.source"), tailwindClasses: "basis-[10%]" }] : []),
            { name: "Actions", label: i18n.t("fields.actions"), tailwindClasses: "basis-[10%] pr-1" }
          ]}
        />
      )}
    </TablePage>
  )
}

export default GeologyCrushing;